import { QUERY_PARAM_NATIVE_EMBED } from '@databases/modes';
import { nanoid } from 'nanoid';

import {
  type RovoContentBridgeAPI,
  client,
  getBrowserBridgeAPI,
} from '@canvas/rovo-content-bridge-api';

import {
  CopyClipboardEventCommand,
  type CopyClipboardEventCommandProperties,
  CutClipboardEventCommand,
  type CutClipboardEventCommandProperties,
} from './commands/clipboard-event-commands.ts';
import {
  DatabaseUIReadyCommand,
  type DatabaseUIReadyCommandProperties,
} from './commands/database-ui-ready-command.ts';
import {
  type DiscardDraftChangesCommandProperties,
  DiscardDraftChangesCommand,
} from './commands/discard-draft-changes-command.ts';
import {
  type DismissActionBarCommandProperties,
  DismissActionBarCommand,
} from './commands/dismiss-action-bar-command.ts';
import { DismissContextMenuCommand } from './commands/dismiss-context-menu-command.ts';
import {
  PointerDownCommand,
  type PointerDownCommandProperties,
  PointerMoveCommand,
  type PointerMoveCommandProperties,
  PointerUpCommand,
  type PointerUpCommandProperties,
} from './commands/drag-event-commands.ts';
import {
  type EnabledFeaturesCommandProperties,
  EnabledFeaturesCommand,
} from './commands/enabled-features-command.ts';
import {
  KeyboardDownCommand,
  type KeyboardDownCommandProperties,
  KeyboardUpCommand,
  type KeyboardUpCommandProperties,
} from './commands/keyboard-event-commands.ts';
import {
  type ShowPanelCommandProperties,
  ShowPanelCommand,
} from './commands/panels/show-panel-command.ts';
import {
  type SaveDraftChangesCommandProperties,
  SaveDraftChangesCommand,
} from './commands/save-draft-changes-command.ts';
import {
  type ShowActionBarCommandProperties,
  ShowActionBarCommand,
} from './commands/show-action-bar-command.ts';
import {
  type ShowDialogCommandProperties,
  ShowDialogCommand,
} from './commands/show-dialog-command.ts';
import {
  type ShowSaveDraftChangesBarCommandProperties,
  ShowSaveDraftChangesBarCommand,
} from './commands/show-save-draft-changes-bar-command.ts';
import {
  type ShowSectionMessageCommandProperties,
  ShowSectionMessageCommand,
} from './commands/show-section-message-command.ts';
import {
  type ShowSnackbarCommandProperties,
  ShowSnackbarCommand,
} from './commands/show-snackbar-command.ts';
import {
  type UpdateSearchFilterCommandProperties,
  UpdateSearchFilterCommand,
} from './commands/update-search-filter-command.ts';
import { UpdateTitleCommand } from './commands/update-title-command.ts';
import {
  type OnViewsStateUpdateCommandProperties,
  OnViewsStateUpdateCommand,
} from './commands/views/on-views-state-update-command.ts';
import {
  type SetCurrentViewCommandProperties,
  SetCurrentViewCommand,
} from './commands/views/set-current-view-command.ts';
import {
  WheelEventCommand,
  type WheelEventCommandProperties,
} from './commands/wheel-event-command.ts';
import {
  OnShowContextMenuCommand,
  type OnShowContextMenuCommandProperties,
} from './commands.ts';
import { appendConfluenceDatabasesQueryParams } from './query-params.ts';
import { type NativeEmbedFeatureList } from './types.ts';
import { mergeEnabledFeatures } from './utils.ts';

export type ClientId = ReturnType<typeof client>;

type DatabaseBridgeAPI = Pick<RovoContentBridgeAPI, 'send' | 'on' | 'off' | 'ready'>;

export type DatabaseIframeClientInput = {
  dbClient: ClientId;
  externalClient: ClientId;
  bridge: DatabaseBridgeAPI;
};

/**
 * @privateRemarks The Rovo package doesn't export the command handler type so we create our own
 */
export type CommandHandler<CommandProperties, ReturnType = any> = (
  a: CommandProperties & { senderId: never; receiverId: never },
) => ReturnType;

/**
 * Bridge client for communicating with embedded databases in the native embed experience.
 *
 * This client enables external applications (E.g. Confluence Whiteboards and Confluence Pages) to interact
 * with embedded databases.
 *
 * @privateRemarks This is purposely designed to be a simple wrapper around Rovo commands
 * to make consumption easier. It handles the complexity of client IDs and message routing
 * while providing a clean API for database interactions.
 */
export class DatabaseBridgeClient {
  /**
   * @internal Only public for testing purposes
   */
  public constructor(private readonly input: DatabaseIframeClientInput) {}

  /**
   * Updates the title of the embedded database.
   *
   * @param title - The new title for the database
   * @returns Promise that resolves when the title update is complete
   */
  updateTitle(title: string) {
    return this.input.bridge.send(UpdateTitleCommand, {
      title,
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Performs feature handshake to enable specific database capabilities.
   *
   * This method should be used during the initial setup to coordinate which features
   * should be enabled between the host application and the embedded database,
   * ensuring safe incremental rollout of new functionality.
   *
   * @param features - Object specifying which features to enable
   * @returns Promise that resolves when Databases has received and the features
   * you would like to enable
   */
  enableFeatures(features: EnabledFeaturesCommandProperties['features']) {
    return this.input.bridge.send(EnabledFeaturesCommand, {
      features,
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Listens to commands to show the database action bar in the host application.
   *
   * The action bar provides contextual actions for selected database entries or
   * fields, such as edit, delete, and formatting options. In the native embed experience,
   * this should appear as part of the host application's UI rather than within the iframe.
   *
   * @param onShowActionBarCallback - Callback to handle action bar display requests
   */
  onShowActionBar(
    onShowActionBarCallback: CommandHandler<ShowActionBarCommandProperties>,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowActionBarCommand,
      onShowActionBarCallback,
    );
  }

  /**
   * Listens to commands to dismiss the currently displayed database action bar.
   *
   * This method hides the action bar, typically called when users deselect
   * entries or click elsewhere in the database interface.
   *
   * @param onDismissActionBarCallback - Callback to handle action bar dismissal requests
   */
  onDismissActionBar(
    onDismissActionBarCallback: CommandHandler<DismissActionBarCommandProperties>,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      DismissActionBarCommand,
      onDismissActionBarCallback,
    );
  }

  /**
   * Discards pending draft changes in the database.
   *
   * This method discards any pending changes made to database content,
   * allowing users to abandon their work without affecting the persisted data.
   *
   * @returns Promise that resolves when the changes are discarded
   */
  discardDraftChanges(
    properties: Omit<DiscardDraftChangesCommandProperties, 'senderId' | 'receiverId'>,
  ) {
    return this.input.bridge.send(DiscardDraftChangesCommand, {
      ...properties,
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Saves pending draft changes to the database.
   *
   * This method persists any pending changes made to database content,
   * ensuring data consistency and allowing users to save their work
   * without leaving the embed context.
   *
   * @param properties - Configuration for saving draft changes
   * @returns Promise that resolves when the changes are saved
   */
  saveDraftChanges(
    properties: Omit<SaveDraftChangesCommandProperties, 'senderId' | 'receiverId'>,
  ) {
    return this.input.bridge.send(SaveDraftChangesCommand, {
      ...properties,
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Listens to notifications when database view configuration changes.
   *
   * This method is triggered when users modify view settings such as filters,
   * sorting, column visibility, or layout changes, ensuring the host application
   * can respond appropriately to view state changes.
   *
   * @param onViewStateUpdateCallback - Callback to handle view change notifications
   */
  onViewStateUpdate(
    onViewStateUpdateCallback: CommandHandler<OnViewsStateUpdateCommandProperties>,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      OnViewsStateUpdateCommand,
      onViewStateUpdateCallback,
    );
  }

  /**
   * Listens to commands to show database configuration panels in the host application.
   *
   * This method displays panels for field settings, sorting, filtering, imports,
   * hiding fields, and layout configuration. In the native embed experience,
   * these panels should appear in the host application's side panel area rather than
   * within the database iframe, providing a more integrated user experience.
   *
   * @param onShowPanelCallback - Callback to handle panel display requests
   * including panel type
   */
  onShowPanel(onShowPanelCallback: CommandHandler<ShowPanelCommandProperties>) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowPanelCommand,
      onShowPanelCallback,
    );
  }

  /**
   * Listens to commands to show a given database-related dialog in the host application.
   *
   * This method displays dialogs for the Jira integration, Confluence,
   * CSV import, and confirmation dialogs. In the native embed experience,
   * these dialogs should appear in the host application's modal system rather than
   * within the database iframe, ensuring proper z-index and 'focus' designs
   * (E.g. scrim over existing content).
   *
   * @param onShowDialogCallback - Configuration including dialog type (E.g. Jira,
   * Confluence, CSV, etc)
   */
  onShowDialog(onShowDialogCallback: CommandHandler<ShowDialogCommandProperties>) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowDialogCommand,
      onShowDialogCallback,
    );
  }

  /**
   * Listens to commands to show database-related notification snackbars in the host application.
   *
   * This method displays notification messages for database operations such as
   * successful saves, error messages, or other status updates. In the native embed
   * experience, these notifications should appear in the host application's notification
   * system rather than within the database iframe.
   *
   * @param onShowSnackbarCallback - Callback to handle snackbar display requests
   */
  onShowSnackbar(onShowSnackbarCallback: CommandHandler<ShowSnackbarCommandProperties>) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowSnackbarCommand,
      onShowSnackbarCallback,
    );
  }

  /**
   * Listens to commands to show the save draft changes notification bar in the host application.
   *
   * This method displays a notification bar that alerts users to unsaved changes
   * and provides options to save or discard their modifications. In the native embed
   * experience, this bar should appear in the host application's UI rather than within
   * the database iframe, maintaining visual consistency with the host's design.
   *
   * @param onShowSaveDraftChangesBarCallback - Callback to handle draft changes bar display requests
   */
  onShowSaveDraftChangesBar(
    onShowSaveDraftChangesBarCallback: CommandHandler<ShowSaveDraftChangesBarCommandProperties>,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowSaveDraftChangesBarCommand,
      onShowSaveDraftChangesBarCallback,
    );
  }

  /**
   * Listens to commands to show database section messages and flags in the host application.
   *
   * This method displays important system messages, warnings, or informational
   * content related to database operations. In the native embed experience,
   * these messages should appear in the host application's flag/message system for
   * better user visibility and consistency.
   *
   * @param onShowSectionMessageCallback - Callback to handle section message display requests
   */
  onShowSectionMessage(
    onShowSectionMessageCallback: CommandHandler<ShowSectionMessageCommandProperties>,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      ShowSectionMessageCommand,
      onShowSectionMessageCallback,
    );
  }

  /**
   * Updates database search and filter criteria in the native embed experience.
   *
   * This method allows users to modify search terms,
   * providing real-time database content filtering that maintains state
   * across the embedded database experience. Changes are reflected immediately
   * in the database view.
   *
   * This command is _not_ related to supporting Pages/Whiteboard/parent-content-type's search
   * and it is NOT referring to the filter settings panel options in Databases.
   * Search-filter refers to the ability to type a phrase into Database but rather:
   * {@link https://www.loom.com/share/0128c83164024a9ba7fcfb57bd96e1cd?sid=d9c0657d-acc8-4667-a5e5-aca75765685b}
   *
   * @param properties - Configuration for the search filter update
   * @returns Promise that resolves when the filter is updated
   */
  updateSearchFilter(properties: Pick<UpdateSearchFilterCommandProperties, 'query'>) {
    return this.input.bridge.send(UpdateSearchFilterCommand, {
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
      query: properties.query,
    });
  }

  /**
   * Changes the active view of the database in the native embed experience.
   *
   * This method allows the host application to switch between
   * different database views (E.g. table, gallery, board, etc), typically triggered
   * from the view switcher in a toolbar somewhere. The database will update its
   * display to show the requested view layout and configuration.
   *
   * @param properties - Configuration for the view change including view identifier
   * @returns Promise that resolves when the view change is complete
   */
  setView(properties: Pick<SetCurrentViewCommandProperties, 'viewId'>) {
    return this.input.bridge.send(SetCurrentViewCommand, {
      ...properties,
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Listens to commands to fetch the enabled features from the databases client. The
   * callback returns a list of features that are enabled in external client (e.g. whiteboards, pages, etc)
   */
  onEnabledFeatures(
    onEnabledFeaturesCallback: CommandHandler<
      EnabledFeaturesCommandProperties,
      NativeEmbedFeatureList
    >,
  ) {
    return this.input.bridge.on(
      this.input.externalClient,
      EnabledFeaturesCommand,
      onEnabledFeaturesCallback,
    );
  }

  /**
   * Listens to wheel events forwarded from the database iframe,
   *
   * This method allows the parent application (e.g. Whiteboards, Pages) to receive
   * wheel events that occur within the database iframe, enabling proper canvas zoom/pan
   * functionality. The database iframe prevents default wheel behavior and forwards
   * the event data through the bridge for the parent to handle.
   *
   * This is particularly useful for maintaining consistent zoom/pan behavior when
   * wheel events occur over embedded database content.
   *
   * @param onWheelEventCallback - Callback to handle wheel events from the database iframe
   * @returns A cleanup function that removes the event listener
   */
  onWheelEvent(
    onWheelEventCallback: CommandHandler<WheelEventCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      WheelEventCommand,
      onWheelEventCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, WheelEventCommand);
    };
  }

  /**
   * Listens to database UI ready notifications from the database iframe.
   *
   * This method is called when the database UI has finished rendering
   * (both initial render and data loading complete), allowing the parent
   * application to hide any loading overlays.
   *
   * @param onDatabaseUIReadyCallback - Callback to handle database UI ready notifications
   * @returns A cleanup function that removes the event listener
   */
  onDatabaseUIReady(
    onDatabaseUIReadyCallback: CommandHandler<DatabaseUIReadyCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      DatabaseUIReadyCommand,
      onDatabaseUIReadyCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, DatabaseUIReadyCommand);
    };
  }

  /**
   * Listens to pointer down events forwarded from the database iframe.
   *
   * This enables drag-and-drop functionality by allowing the parent application
   * to receive pointer events that occur within the database iframe. The database
   * iframe forwards these events through the bridge so the parent can handle
   * dragging of the entire database element.
   *
   * @param onPointerDownCallback - Callback to handle pointer down events
   * @returns A cleanup function that removes the event listener
   */
  onPointerDown(
    onPointerDownCallback: CommandHandler<PointerDownCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      PointerDownCommand,
      onPointerDownCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, PointerDownCommand);
    };
  }

  /**
   * Listens to pointer move events forwarded from the database iframe.
   *
   * This enables drag-and-drop functionality by continuously forwarding pointer
   * movement during a drag operation. The database iframe forwards these events
   * through the bridge so the parent can properly update the position during drag.
   *
   * @param onPointerMoveCallback - Callback to handle pointer move events
   * @returns A cleanup function that removes the event listener
   */
  onPointerMove(
    onPointerMoveCallback: CommandHandler<PointerMoveCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      PointerMoveCommand,
      onPointerMoveCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, PointerMoveCommand);
    };
  }

  /**
   * Listens to pointer up events forwarded from the database iframe.
   *
   * This enables drag-and-drop functionality by detecting when a drag operation
   * ends. The database iframe forwards these events through the bridge so the
   * parent can finalize the drag operation and re-enable iframe interactions.
   *
   * @param onPointerUpCallback - Callback to handle pointer up events
   * @returns A cleanup function that removes the event listener
   */
  onPointerUp(
    onPointerUpCallback: CommandHandler<PointerUpCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      PointerUpCommand,
      onPointerUpCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, PointerUpCommand);
    };
  }

  /**
   * Listens to keyboard down events forwarded from the database iframe.
   *
   * This enables keyboard interactions like deletion by allowing the parent application
   * to receive keyboard events that occur within the database iframe. The database
   * iframe forwards these events through the bridge so the parent can handle
   * keyboard shortcuts like backspace/delete for element deletion.
   *
   * @param onKeyboardDownCallback - Callback to handle keyboard down events
   * @returns A cleanup function that removes the event listener
   */
  onKeyboardDown(
    onKeyboardDownCallback: CommandHandler<KeyboardDownCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      KeyboardDownCommand,
      onKeyboardDownCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, KeyboardDownCommand);
    };
  }

  /**
   * Listens to keyboard up events forwarded from the database iframe.
   *
   * This enables keyboard interactions by allowing the parent application
   * to receive keyboard events that occur within the database iframe.
   *
   * @param onKeyboardUpCallback - Callback to handle keyboard up events
   * @returns A cleanup function that removes the event listener
   */
  onKeyboardUp(
    onKeyboardUpCallback: CommandHandler<KeyboardUpCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      KeyboardUpCommand,
      onKeyboardUpCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, KeyboardUpCommand);
    };
  }

  /**
   * Sends a command to dismiss the context menu inside of the database iframe
   *
   * This allows the ability to dismiss the database context menu whenever the
   * parent application is showing their own context menu.
   */
  dismissContextMenu() {
    return this.input.bridge.send(DismissContextMenuCommand, {
      senderId: this.input.externalClient,
      receiverId: this.input.dbClient,
    });
  }

  /**
   * Listens to when a context menu is being shown in the database iframe.
   *
   * Useful if you need to dismiss your own application's context menu
   *
   * @param onShowContextMenuCallback - Callback to handle context menu dismissal
   * @returns A cleanup function that removes the event listener
   */
  onShowContextMenu(
    onShowContextMenuCallback: CommandHandler<OnShowContextMenuCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      OnShowContextMenuCommand,
      onShowContextMenuCallback,
    );

    return () => {
      this.input.bridge.off(this.input.externalClient, OnShowContextMenuCommand);
    };
  }

  /**
   * Listens to copy clipboard events forwarded from the database iframe.
   *
   * This is called when databases has decided not to handle a copy event
   * You can use this to handle copying the entire database element rather than
   * its content.
   *
   * @param onCopyClipboardCallback - Callback to handle copy clipboard events
   * @returns A cleanup function that removes the event listener
   */
  onCopyClipboard(
    onCopyClipboardCallback: CommandHandler<CopyClipboardEventCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      CopyClipboardEventCommand,
      onCopyClipboardCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, CopyClipboardEventCommand);
    };
  }

  /**
   * Listens to cut clipboard events forwarded from the database iframe.
   *
   * This is called when databases has decided not to handle a cut event
   * You can use this to handle cutting the entire database element rather than
   * its content.
   *
   * @param onCutClipboardCallback - Callback to handle cut clipboard events
   * @returns A cleanup function that removes the event listener
   */
  onCutClipboard(
    onCutClipboardCallback: CommandHandler<CutClipboardEventCommandProperties>,
  ): () => void {
    this.input.bridge.on(
      this.input.externalClient,
      CutClipboardEventCommand,
      onCutClipboardCallback,
    );
    return () => {
      this.input.bridge.off(this.input.externalClient, CutClipboardEventCommand);
    };
  }

  public get dbClientId() {
    return this.input.dbClient;
  }
}

export type InitialDatabaseClientInput = {
  dbClientId: string;
  externalClientId: string;
};

export type ReadyDatabaseClientInput = {
  bridgeClient: DatabaseBridgeClient;
  dbClientId: string;
  externalClientId: string;
  initialFeaturesToEnable: NativeEmbedFeatureList;
};

export type UpgradeInput = {
  /**
   * These should be the features that we're ready to use in whiteboards
   * Note this this is NOT the same as the set of features that we'll want to
   * actually _show_ in Whiteboards/Pages since it requires us to be ready for Databases
   */
  featuresReadyToBeUsed: NativeEmbedFeatureList;
  /**
   * Will avoid merging in the iframe's communicated features into your apps state.
   * Used for debugging purposes in case the other side of the iframe isn't
   * sending the correct feature set yet.
   */
  debugDontMergeFeatures?: boolean;
};

/**
 * The high-level client for interacting with databases in the native embed experience.
 *
 * This client provides the main interface for external applications (like
 * Confluence Whiteboards and Pages) to interact with embedded databases.
 * It manages the bridge client for sending commands and provides utilities
 * for embedding databases with proper query parameters and configuration.
 *
 * The {@link InitialDatabaseClient} is typically created using the {@link createDatabaseClient} factory
 * function, which handles the setup of unique client IDs, bridge API initialization,
 * and feature handshaking.
 *
 * The database client begins in an "Initial" state that can append embed query params but cannot use bridge API yet.
 * Use {@link upgrade} to give yourself an {@link ReadyDatabaseClient} with bridge capabilities.
 *
 * @privateRemarks Try to maximize external facing class documentation on
 * {@link InitialDatabaseClient} and {@link createDatabaseClient} instead of {@link ReadyDatabaseClient} as it's
 * the first things that consumers of the native database embeds will have to
 * interact with.
 * @privateRemarks The intention regarding the split between
 * {@link InitialDatabaseClient} and {@link ReadyDatabaseClient} is to hand-hold
 * implementors of native database embeds through the process of implementing
 * native database embeds as much as possible and design an API that is
 * essentially impossible to use incorrectly.
 */
export class InitialDatabaseClient {
  public constructor(private readonly input: InitialDatabaseClientInput) {}

  public appendEmbedQueryParams(searchParams: URLSearchParams) {
    appendConfluenceDatabasesQueryParams(searchParams, {
      bridge: {
        externalClientId: this.input.externalClientId,
        localClientId: this.input.dbClientId,
      },
      presentationMode: QUERY_PARAM_NATIVE_EMBED,
    });
  }

  public async upgrade(input: UpgradeInput): Promise<ReadyDatabaseClient> {
    // Use Playwright mock bridge if available (for integration tests)
    const mockBridge = globalThis.ATLASSIAN_CANVAS?.mockBridgeAPI as DatabaseBridgeAPI;
    const rawBridge = mockBridge ?? getBrowserBridgeAPI();

    const externalClient = client(this.input.externalClientId);
    const bridgeClient = new DatabaseBridgeClient({
      dbClient: client(this.input.dbClientId),
      externalClient,
      bridge: rawBridge,
    });

    // Signal to bridge that external client (e.g. Whiteboards, Pages, etc) hosting Database is ready
    rawBridge.ready(externalClient);

    // Note: We tackle making use of this value in a subsequent step
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const databasesFeatures = await new Promise<NativeEmbedFeatureList>((resolve) =>
      bridgeClient.onEnabledFeatures((databasesFeatures) => {
        resolve(databasesFeatures.features);
        return input.featuresReadyToBeUsed;
      }),
    );

    const initialFeaturesToEnable = input.debugDontMergeFeatures
      ? input.featuresReadyToBeUsed
      : mergeEnabledFeatures(input.featuresReadyToBeUsed, databasesFeatures);

    return new ReadyDatabaseClient({
      bridgeClient,
      dbClientId: this.input.dbClientId,
      externalClientId: this.input.externalClientId,
      initialFeaturesToEnable,
    });
  }
}

/**
 * Ready database client with full bridge API access.
 * Obtained by calling {@link upgrade} on an {@link InitialDatabaseClient}.
 */
export class ReadyDatabaseClient {
  public constructor(private readonly input: ReadyDatabaseClientInput) {}

  /**
   * Gets the bridge client for sending commands to the embedded database.
   *
   * @returns The DatabaseBridgeClient instance for command communication
   */
  public get bridge() {
    return this.input.bridgeClient;
  }

  public get features() {
    return this.input.initialFeaturesToEnable;
  }

  /**
   * Appends the necessary query parameters for embedding databases in native mode.
   *
   * This method configures the URL with the required parameters for the native embed
   * experience, including bridge client IDs and presentation mode settings.
   *
   * @param searchParams - URLSearchParams object to modify with embed parameters
   */
  public appendEmbedQueryParams(searchParams: URLSearchParams) {
    appendConfluenceDatabasesQueryParams(searchParams, {
      bridge: {
        externalClientId: this.input.externalClientId,
        localClientId: this.input.dbClientId,
      },
      presentationMode: QUERY_PARAM_NATIVE_EMBED,
    });
  }
}

/**
 * Factory function to create a DatabaseClient with default configuration for native embed.
 *
 * The factory handles all the complexity of setting up the bridge communication
 * between the host application and the embedded database, ensuring proper
 * message routing and feature coordination for the native embed experience.
 *
 * @see {@link InitialDatabaseClient} for further documentation about the database client's consumption
 */
export const createDatabaseClient = (): InitialDatabaseClient => {
  const id = nanoid();
  const dbClientId = `database-${id}`;
  const externalClientId = `parent-${id}`;

  return new InitialDatabaseClient({ dbClientId, externalClientId });
};
